// HM-319.r cluster 15.4: Dashboard rework SPEC mockup v3.
// Rimossa griglia camere (vivra' nella nuova pagina /camere cluster 15.5).
// Rimosso RoomDetailPanel (verra' refactorato come Drawer triggherato
// solo da pagina Camere in HM-324).
// Rimosso filterStatuses state (era dead post-rework).
//
// Layout: Hero CTA "Assegna Camera" + 5 KPI + 2 info cards
// (ProssimiArrivi + StatoSistema). La logica live (WS, walk-in,
// checkout, shortcuts) e' tutta preservata invariata: i power user
// usano i keyboard shortcut c/o per check-in/out diretti, e il bottone
// CTA per walk-in piano-first.

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type RoomDb, fetchBookingsWindow, fetchRooms } from '../api/rooms.js';
import { CheckoutModal } from '../components/checkout/CheckoutModal.js';
import { HeroAssignRoom } from '../components/dashboard-v2/HeroAssignRoom.js';
import { InfoCardsRow } from '../components/dashboard-v2/InfoCards.js';
import { KpiStrip } from '../components/dashboard-v2/KpiStrip.js';
import { useDashboardKpi } from '../components/dashboard-v2/useDashboardKpi.js';
import { ConflictBadge } from '../components/dashboard/ConflictBadge.js';
import { ShortcutsHelp } from '../components/dashboard/ShortcutsHelp.js';
import { ToastHost } from '../components/dashboard/ToastHost.js';
import { WalkInModal } from '../components/walk-in/WalkInModal.js';
import { useDashboardLiveSync } from '../hooks/useDashboardLiveSync.js';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts.js';
import { useWsConnection } from '../hooks/useWsEvents.js';
import '../styles/blink.css';
import { useCheckoutModal } from '../stores/checkout.js';
import { useWalkInWizard } from '../stores/walkIn.js';

export function DashboardPage() {
  const ws = useWsConnection();
  const liveSync = useDashboardLiveSync(ws);
  const openWalkIn = useWalkInWizard((s) => s.open);
  const openCheckout = useCheckoutModal((s) => s.open);
  const [helpOpen, setHelpOpen] = useState(false);
  const now = useNowTick(60_000);
  const windowEnd = useMemo(() => new Date(now.getTime() + 8 * 60 * 60 * 1000), [now]);

  const roomsQuery = useQuery({ queryKey: ['rooms'], queryFn: fetchRooms });
  const bookingsQuery = useQuery({
    queryKey: ['bookings', { window: '8h' }],
    queryFn: () =>
      fetchBookingsWindow({
        from: now.toISOString(),
        to: windowEnd.toISOString(),
      }),
    staleTime: 30_000,
  });

  const kpis = useDashboardKpi({
    rooms: roomsQuery.data?.rooms ?? [],
    bookings: bookingsQuery.data?.bookings ?? [],
    now,
  });

  // Power user: shortcut c/o per check-in/out diretti via tile focused.
  // La griglia ora vive in /camere ma i shortcut funzionano comunque
  // navigando li' o restando in /dashboard se il focus e' su un tile
  // attivo (es. dopo navigation).
  const triggerContextualAction = useCallback(
    (kind: 'checkin' | 'checkout') => {
      const focused = document.activeElement as HTMLElement | null;
      const code = focused?.dataset.testid?.replace('room-tile-', '');
      const room = code ? roomsQuery.data?.rooms.find((r: RoomDb) => r.code === code) : undefined;
      if (!room) {
        setHelpOpen(true);
        return;
      }
      if (kind === 'checkin' && room.status === 'free') {
        openWalkIn(room);
        return;
      }
      if (kind === 'checkout' && room.status === 'occupied') {
        const b = (bookingsQuery.data?.bookings ?? []).find(
          (x) => x.roomId === room.id && x.status === 'checked_in',
        );
        if (b) {
          openCheckout(room, b);
          return;
        }
      }
      setHelpOpen(true);
    },
    [openWalkIn, openCheckout, roomsQuery.data, bookingsQuery.data],
  );

  useKeyboardShortcuts({
    onToggleHelp: () => setHelpOpen((v) => !v),
    onCloseHelp: () => setHelpOpen(false),
    onCheckin: () => triggerContextualAction('checkin'),
    onCheckout: () => triggerContextualAction('checkout'),
  });

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="dashboard-v2">
      {/* Banner WS / conflicts: visibili solo quando attivi */}
      {(ws.status !== 'open' || liveSync.conflicts.length > 0) && (
        <div className="shrink-0 px-6 pt-4 space-y-2">
          {ws.status !== 'open' && (
            <div
              role="alert"
              data-testid="ws-banner"
              className="rounded-md px-3 py-2 text-sm bg-accent-soft text-accent-primary-text border border-border-base"
            >
              {ws.status === 'connecting' && 'Connessione WS in corso…'}
              {ws.status === 'reconnecting' && 'Riconnessione…'}
              {ws.status === 'closed' && 'Connessione WS persa. Riprovo…'}
            </div>
          )}
          {liveSync.conflicts.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <ConflictBadge count={liveSync.conflicts.length} />
            </div>
          )}
        </div>
      )}

      {/* HM-600 iPad UX: hero + KPI fissi (shrink-0), scorre solo InfoCardsRow
          quando il contenuto eccede l'altezza disponibile. */}
      <div className="shrink-0 px-6 pt-5 space-y-5 max-xl:px-3 max-xl:pt-3 max-xl:space-y-3">
        <HeroAssignRoom />
        <KpiStrip kpis={kpis} />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-5 pb-6 max-xl:px-3 max-xl:pt-3 max-xl:pb-3">
        <InfoCardsRow
          rooms={roomsQuery.data?.rooms ?? []}
          bookings={bookingsQuery.data?.bookings ?? []}
          halAlerts={liveSync.halAlerts}
          now={now}
        />
      </div>

      <WalkInModal />
      <CheckoutModal />
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      <ToastHost toasts={liveSync.toasts} onDismiss={liveSync.dismissToast} />
    </div>
  );
}

/** Hook minimale: ritorna `new Date()` aggiornato ogni `intervalMs`. */
function useNowTick(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
